
import { Vue, Component, Watch, Prop } from 'vue-property-decorator';
import { State, Action, Getter, Mutation } from 'vuex-class';
import { Item, ItemFilterType } from '@/store/models/ItemModel';
import { SearchRequestModel } from '@/store/models/OrderlineModel';
import { OrdersService } from '@/services/orders-service';
import { ErrorService } from '@/services/error-service';
import DefaultLayout from '@/components/DefaultLayout.vue';
import J1LPagination from '@/components/J1LPagination.vue';
import ItemCard from '@/components/cardComponents/ItemCard.vue';
import J1lImg from '@/components/J1lImg.vue';
import NoItemOrder from '@/components/NoItemOrder.vue';
import AlertComponent from '@/components/AlertLayout.vue';
import J1LSearch from '@/components/vuetifyOverrides/J1LSearch.vue';

@Component({
  components: {
    DefaultLayout,
    J1LPagination,
    ItemCard,
    J1lImg,
    AlertComponent,
    NoItemOrder,
    J1LSearch
  }
})
export default class ManageItems extends Vue {
  $refs!: {
    defaultActions: HTMLFormElement;
    J1LSearch: HTMLFormElement;
    itemCard: HTMLFormElement;
  };

  /* Properties */
  /* Store Actions */
  @State('shouldReconcileQueuedItems', { namespace: 'items' })
  shouldReconcileQueuedItems: any;
  @Getter('getLoadingState', { namespace: 'items' })
  areItemsLoading: any;
  @Getter('getItemFilterCountContainer', { namespace: 'items' })
  getItemFilterCountContainer: any;
  @Action('ReconcileItems', { namespace: 'items' })
  ReconcileItems: any;
  @Action('GetShoppingCart', { namespace: 'orderlines' })
  refreshShoppingCart!: () => Promise<any>;
  @Getter('getAllProcessingImageIds', { namespace: 'items' })
  processingImageItemIds: any;
  @Getter('items', { namespace: 'items' })
  items!: Item[];
  @Getter('getFilteredItem', { namespace: 'items' })
  GetFilteredItem: any;
  @Getter('doesCustomerHaveItems', { namespace: 'items' })
  doesCustomerHaveItems!: boolean;
  @Mutation('setHasItems', { namespace: 'items' })
  setHasItems: any;

  /* Watchers */
  @Watch('getItemFilterCountContainer')
  onFilterCountChange(val: any) {
    this.refreshFilterOptionCount();
  }
  @Watch('GetFilteredItem')
  onFilteredItemChange(val: any) {
    if (val[0]) {
      this.activeChip = 0;
      this.isItemFiltered = true;
      this.filteredItems = val;
    }
  }

  /* Data */
  isItemFiltered: boolean = false;
  areChipsGettingData: boolean = false;
  shouldRefreshImg: boolean = false;
  shouldStopPagination: boolean = false;
  isLoading: boolean = false;
  filteredItems: Item[] = [];
  activeChip: number = 0;
  searchModel: SearchRequestModel | null = null;
  currentFilter: {
    id: number;
    text: string;
    value: string;
    count: number;
    isSelected: true;
  } = {
    id: 0,
    text: 'Everything',
    value: 'Everything',
    count: 0,
    isSelected: true
  };
  filterOptions: any[] = [
    {
      id: 0,
      text: 'Everything',
      value: 'Everything',
      count: 0,
      isSelected: true
    },
    {
      id: 1,
      text: 'On Order',
      value: 'OnOrder',
      count: 0,
      isSelected: false
    },
    {
      id: 2,
      text: 'On Hold',
      value: 'OnHold',
      count: 0,
      isSelected: false
    },
    {
      id: 3,
      text: 'Sign-Offs',
      value: 'SignOffs',
      count: 0,
      isSelected: false
    }
  ];

  selectedItemIdAndType: any = null;

  /* Methods */
  refreshCart() {
    this.$refs.defaultActions.getOrderlinesInCart();
  }
  refreshFilterOptionCount() {
    this.filterOptions.map((fo, i) => {
      fo.count = this.getItemFilterCountContainer[fo.value];
    });
  }
  search(model: SearchRequestModel) {
    this.refreshPagination();
    this.searchModel = model;
    if (!model) this.clearFilteredItems();
  }
  getFilteredItems(model: SearchRequestModel) {
    this.$store.commit('items/setCurrentPage', 1);
    this.refreshPagination();
    this.$store.dispatch('items/ClearSearchOrder');
    this.currentFilter = this.filterOptions[0];
    this.searchModel = model;
  }
  clearFilteredItems() {
    this.$store.dispatch('items/ClearSearchOrder');
    this.searchModel = null;
    this.isItemFiltered = false;
    this.currentFilter = this.filterOptions[0];
  }
  refreshPagination() {
    this.shouldStopPagination = true;
    setTimeout(() => {
      this.shouldStopPagination = false;
    }, 1);
  }
  async onItemFilterOptionClick(value: any) {
    // get rid of search filter action and state
    this.filterOptions[value].isSelected = true;
    switch (value) {
      case 0:
        this.$store.commit('items/setFilteringType', ItemFilterType.Everything);
        this.isItemFiltered = false;
        break;
      case 1:
        this.areChipsGettingData = true;
        await this.$store.dispatch('items/GetOnOrderItem');
        this.$store.commit('items/setFilteringType', ItemFilterType.OnOrder);
        this.filteredItems = this.GetFilteredItem;
        this.isItemFiltered = true;
        this.areChipsGettingData = false;
        break;
      case 2:
        this.areChipsGettingData = true;
        await this.$store.dispatch('items/GetOnHoldItem');
        this.$store.commit('items/setFilteringType', ItemFilterType.OnHold);
        this.filteredItems = this.GetFilteredItem;
        this.isItemFiltered = true;
        this.areChipsGettingData = false;
        break;
      case 3:
        this.areChipsGettingData = true;
        await this.$store.dispatch('items/GetSignOffItem');
        this.$store.commit('items/setFilteringType', ItemFilterType.SignOffs);
        this.filteredItems = this.GetFilteredItem;
        this.isItemFiltered = true;
        this.areChipsGettingData = false;
        break;
    }
  }
  async handleEditItem(item: any, isAdding: boolean) {
    if (item && !isAdding) {
      this.$refs.J1LSearch.clearSearch();
      await this.$store.dispatch('items/refreshItem', item.itemId);
      this.reGrabItemFromStore(item.itemId);
      this.isItemVersionCorrect(item.itemId, item.expectedVersion);
      if (this.shouldRefreshImg) {
        this.$store.dispatch('items/addProcessImageItemId', item.itemId);
        this.shouldRefreshImg = false;
      }
    }
  }
  isItemVersionCorrect(itemId: string, expectedVersion: number) {
    let item = this.items.find(i => i.Id == itemId);
    if (!item || item.Version !== expectedVersion) {
      ErrorService.LogError(
        `item ${itemId} does not match expected Version ${expectedVersion}`
      );
    }
  }
  async createItem(response: any) {
    if (response.didAccept) {
      this.setHasItems(true);
      await this.$store.dispatch('items/refreshItem', response.itemId);
      this.$store.dispatch('items/addProcessImageItemId', response.itemId);
    }
  }
  setShouldItemRefreshToTrue(response: boolean) {
    this.shouldRefreshImg = response;
  }

  goToOrderView(po: string = '') {
    this.refreshShoppingCart();
    this.$router.push({ name: 'OrderView' });
  }
  goToNewItem() {
    this.$router.push({ name: 'NewItem' });
  }

  async loadItemCard(response: any) {
    if (this.processingImageItemIds.length > 0) {
      await this.$store.dispatch('items/removeProcessImageItemId', response.itemId);
    }
    this.$store.dispatch('items/refreshItem', response.itemId);
  }
  get displayedItems() {
    return this.isItemFiltered ? this.filteredItems : this.items;
  }
  get filteredfilterOptions() {
    return this.filterOptions.filter(f => f.count > 0);
  }
  dropZoneEvent() {
    console.log('File Drop');
  }
  reGrabItemFromStore(itemId: string) {
    let itemFromStore = this.items.find(i => i.Id === itemId);
    if (itemFromStore && this.isItemFiltered) {
      this.filteredItems = this.filteredItems.filter(i => i.Id !== itemId);
      this.displayedItems.unshift(itemFromStore);
    }
  }
  handlePartNumberSearch(partnumber: string) {
    this.$refs.J1LSearch.setSearchText(partnumber);
  }

  /* Loaders */
  /* Mounted */
  async mounted() {
    this.$store.dispatch('items/refreshItemFilterCount');
    this.currentFilter = this.filterOptions[0];
    this.onItemFilterOptionClick(0);
    if (this.$route.params.partnumber) {
      this.handlePartNumberSearch(this.$route.params.partnumber);
    }
  }
  /* Created */
}
